import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddRole from './resolvers/resAddRole'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import { apiUrls } from 'consts'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import AddRemoveTableSortable from 'components/AddRemoveTableSortable'
import AddPermission from 'pages/AddPermission'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import prepareSubmitFormFromModal from 'components/EditModal/utils/prepareSubmitFormFromModal'
import Entity from 'components/AddRemoveTableSortable/types/Entity'

const submitRef = React.createRef<HTMLButtonElement>()

const AddRole: React.FunctionComponent<ModalEditableProps> = React.forwardRef(
  (props: ModalEditableProps, ref) => {
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [relationalPermissions, setRelationalPermissions] = useState<Array<Entity>>([])

    const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

    const { id } = useParams()

    const navigate = useNavigate()

    const [roleId] = useState<string | undefined>(props.modalConfig?.entityId ?? id)

    const [colAttributes] = useState<any>(props.modalConfig !== undefined ? { md: 12 } : undefined)

    const {
      register,
      getValues,
      handleSubmit,
      reset,
      setFocus,
      setError,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: resAddRole,
    })

    useInit({
      getValues,
      id: roleId,
      reset,
      setDataLoaded,
      setShowOverlay,
      currentCustomer,
    })

    useFocusError({
      errors,
      setFocus,
    })

    const renderView = (innerContent: any) => {
      return props.modalConfig === undefined ? (
        <Layout
          footerContent={<Footer />}
          brandContent={getBrandContent()}
          showOverlay={showOverlay}
          showCardHeader={true}
          headerInsert={<BackButton url='/roles' text='All Roles' />}
          actionInsert={
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save Role
            </Button>
          }
        >
          {innerContent}
        </Layout>
      ) : (
        innerContent
      )
    }

    return renderView(
      <>
        <Form
          onSubmit={handleSubmit((data) => {
            const commonParams = {
              submitForm,
              data,
              setShowOverlay,
              setError,
              id: roleId,
              name: 'Role',
              successNav: '/roles',
              extraData: {
                permissions: relationalPermissions,
              },
            }

            return props.modalConfig !== undefined
              ? prepareSubmitFormFromModal({
                  ...commonParams,
                  modalConfig: props.modalConfig,
                })
              : prepareSubmitForm({
                  ...commonParams,
                  navigate: navigate,
                })
          })}
          id='main-form'
        >
          <InputWrapper colAttributes={colAttributes} label='Name' name='name' errors={errors}>
            <Form.Control type='text' {...register('name')} />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Active' name='active' errors={errors}>
            <Form.Select {...register('active')}>
              <option key={0} value={1}>
                Yes
              </option>
              <option key={1} value={0}>
                No
              </option>
            </Form.Select>
          </InputWrapper>

          <Button
            type='submit'
            name='save'
            ref={(ref as any) ?? submitRef}
            className='d-none'
          ></Button>
        </Form>

        {props.modalConfig === undefined && (
          <AddRemoveTableSortable
            getAllRowsUrl={apiUrls.permissionsToRoles + '?id=' + id}
            addRemoveRowUrl={apiUrls.permissionsToRoles}
            relationalFieldName='permission_id'
            toggleActiveUrl={apiUrls.permissions}
            maxTableHeight='600px'
            entityNameSingular='Permission'
            entityNamePlural='Permissions'
            setShowOverlay={setShowOverlay}
            editFormComponent={AddPermission}
            onAddItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                    setRelationalPermissions((prevState) => [...prevState, entity])
                    return true
                  }
            }
            onRemoveItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                    setRelationalPermissions((prevState) =>
                      prevState.filter((r) => r.id !== entity.id),
                    )
                    return true
                  }
            }
          />
        )}
      </>,
    )
  },
)

export default AddRole
